import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";

const JENIS = [
  { v: "gaji", l: "Gaji / Pekerjaan" },
  { v: "usaha", l: "Usaha / Pekerjaan Bebas (Freelance)" },
  { v: "investasi", l: "Investasi / Bunga / Dividen" },
  { v: "sewa", l: "Sewa" },
  { v: "hadiah", l: "Hadiah" },
  { v: "lainnya", l: "Lainnya" },
];
const PERLAKUAN = [
  { v: "progresif", l: "Objek Pajak (Progresif)" },
  { v: "final", l: "PPh Final" },
  { v: "bukan_objek", l: "Bukan Objek Pajak" },
];
const defaultPerlakuan = (j) => ({ gaji: "progresif", usaha: "final", investasi: "final", sewa: "final", hadiah: "final", lainnya: "progresif" }[j] || "progresif");
const defaultTarif = (j) => ({ investasi: 20, sewa: 10, hadiah: 0 }[j] ?? 0);
const jenisLabel = (v) => JENIS.find(x => x.v === v)?.l || v || "-";

const empty = {
  tanggal: todayISO(), nama_pembeli: "", nama_barang: "", harga: 0,
  kategori_id: "", kategori_nama: "", keterangan: "", status_pembayaran: "lunas",
  jenis_penghasilan: "gaji", perlakuan_pajak: "progresif", tarif_final_persen: 0, akun_id: "",
};

const badgeCls = { progresif: "bg-blue-50 text-blue-700", final: "bg-amber-50 text-amber-700", bukan_objek: "bg-slate-100 text-slate-600" };

export default function PemasukanPribadi() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [kategori, setKategori] = useState([]);
  const [akun, setAkun] = useState([]);
  const [presets, setPresets] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const [a, k, ak, cfg] = await Promise.all([api.get("/pemasukan"), api.get("/kategori", { params: { tipe: "pemasukan" } }), api.get("/akun"), api.get("/pajak-config")]);
    setItems(a.data); setKategori(k.data); setAkun(ak.data); setPresets(cfg.data?.final_presets || []);
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const setJenis = (v) => setForm((f) => ({ ...f, jenis_penghasilan: v, perlakuan_pajak: defaultPerlakuan(v), tarif_final_persen: defaultTarif(v) }));

  const openNew = () => { setEditId(null); setForm({ ...empty, tanggal: todayISO() }); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    if (!form.nama_pembeli?.trim()) { toast.error("Sumber penghasilan wajib diisi"); return; }
    if (!(Number(form.harga) > 0)) { toast.error("Nominal wajib diisi (lebih dari 0)"); return; }
    let no_invoice_num = form.no_invoice_num;
    try {
      if (!editId) { const r = await api.get("/pemasukan/next-invoice"); no_invoice_num = r.data.next_num; }
      const payload = {
        ...form, no_invoice_num,
        nama_barang: form.nama_barang?.trim() || jenisLabel(form.jenis_penghasilan),
        harga: Number(form.harga), diskon_persen: 0, pajak_persen: 0,
        tarif_final_persen: Number(form.tarif_final_persen) || 0,
      };
      if (editId) await api.put(`/pemasukan/${editId}`, payload);
      else await api.post("/pemasukan", payload);
      toast.success(editId ? "Data diperbarui" : "Pemasukan tersimpan");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delOne = async (id) => {
    if (!confirm("Hapus data ini?")) return;
    await api.delete(`/pemasukan/${id}`); toast.success("Terhapus"); load();
  };

  const filtered = items.filter((it) => {
    const s = q.trim().toLowerCase();
    return !s || [it.nama_pembeli, it.nama_barang, it.kategori_nama, it.tanggal].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(filtered, 10);
  const isUsaha = form.jenis_penghasilan === "usaha";
  const isFinal = form.perlakuan_pajak === "final";

  return (
    <>
      <PageHeader title="Pemasukan Pribadi" subtitle="Catat penghasilan pribadi beserta klasifikasi pajaknya." actions={
        <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-pemasukan-btn"><Plus size={14} className="mr-1" /> Tambah</Button>
      } />
      <div className="p-4 md:p-8 space-y-4">
        <div className="card-flat p-4 relative">
          <Search size={14} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Cari sumber, deskripsi, tanggal..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="pmk-search" />
        </div>
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs md:text-sm" data-testid="pemasukan-table">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="p-3">Tanggal</th><th className="p-3">Jenis</th><th className="p-3">Perlakuan Pajak</th>
                <th className="p-3">Sumber</th><th className="p-3 text-right">Nominal</th><th className="p-3 text-right">Aksi</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400">Tidak ada data.</td></tr>}
                {paged.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 whitespace-nowrap">{tanggalID(p.tanggal)}</td>
                    <td className="p-3">{jenisLabel(p.jenis_penghasilan)}</td>
                    <td className="p-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-sm font-medium ${badgeCls[p.perlakuan_pajak] || badgeCls.progresif}`}>
                        {p.jenis_penghasilan === "usaha" ? "Usaha (ikut pengaturan)" : (PERLAKUAN.find(x => x.v === p.perlakuan_pajak)?.l || "Progresif")}
                      </span>
                    </td>
                    <td className="p-3">{p.nama_pembeli}<div className="text-[11px] text-slate-400">{p.nama_barang}</div></td>
                    <td className="p-3 text-right font-mono font-semibold text-emerald-700">{rupiah(p.total ?? p.harga)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openEdit(p)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-pmk-${p.id}`}><Pencil size={14} /></button>
                        <button onClick={() => delOne(p.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-pmk-${p.id}`}><Trash2 size={14} className="text-rose-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="pemasukan-pagination" />
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pemasukan-dialog">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Pemasukan Pribadi</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} /></div>
            <div className="space-y-1.5">
              <Label>Jenis Penghasilan <span className="text-rose-500">*</span></Label>
              <Select value={form.jenis_penghasilan} onValueChange={setJenis}>
                <SelectTrigger data-testid="pmk-jenis"><SelectValue /></SelectTrigger>
                <SelectContent>{JENIS.map((j) => <SelectItem key={j.v} value={j.v}>{j.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!isUsaha ? (
              <div className="space-y-1.5">
                <Label>Perlakuan Pajak</Label>
                <Select value={form.perlakuan_pajak} onValueChange={(v) => setForm((f) => ({ ...f, perlakuan_pajak: v }))}>
                  <SelectTrigger data-testid="pmk-perlakuan"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERLAKUAN.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Perlakuan Pajak</Label>
                <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2.5">Mengikuti <b>Pengaturan Pajak</b> (metode usaha: UMKM 0,5% / NPPN / Pembukuan).</div>
              </div>
            )}
            {!isUsaha && isFinal && (
              <div className="space-y-1.5 col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Preset Tarif Final</Label>
                  <Select value={String(form.tarif_final_persen)} onValueChange={(v) => setForm((f) => ({ ...f, tarif_final_persen: Number(v) }))}>
                    <SelectTrigger data-testid="pmk-preset-final"><SelectValue placeholder="Pilih preset" /></SelectTrigger>
                    <SelectContent>{presets.map((p, i) => <SelectItem key={i} value={String(p.tarif_persen)}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Tarif PPh Final (%)</Label><Input type="number" value={form.tarif_final_persen} onChange={upd("tarif_final_persen")} data-testid="pmk-tarif-final" /></div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Kategori (opsional)</Label>
              <Select value={form.kategori_id} onValueChange={(v) => { const k = kategori.find((x) => x.id === v); setForm((f) => ({ ...f, kategori_id: v, kategori_nama: k?.nama || "" })); }}>
                <SelectTrigger><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent>{kategori.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Masuk ke Akun (opsional)</Label>
              <Select value={form.akun_id} onValueChange={(v) => setForm((f) => ({ ...f, akun_id: v }))}>
                <SelectTrigger data-testid="pmk-akun"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                <SelectContent>{akun.map((a) => <SelectItem key={a.id} value={a.id}>{a.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Sumber Penghasilan (pemberi/klien) <span className="text-rose-500">*</span></Label><Input value={form.nama_pembeli} onChange={upd("nama_pembeli")} placeholder="mis. PT Sumber Rejeki / Klien A" data-testid="pmk-sumber" /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Deskripsi</Label><Input value={form.nama_barang} onChange={upd("nama_barang")} placeholder="mis. Gaji bulanan / Jasa desain" /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Nominal (Rp) <span className="text-rose-500">*</span></Label><CurrencyInput value={form.harga} onValueChange={upd("harga")} /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
            <Button onClick={save} className="brand-btn rounded-sm" data-testid="pemasukan-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
